import { useEffect, useState, FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authService, agencyService, invitesService, publicAgencyService } from '../services/api';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { GOOGLE_CLIENT_ID } from '@/lib/env';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Users, Shield, Zap } from 'lucide-react';

interface InviteInfo {
  invite_id: string;
  agency_id: string;
  email: string;
  role: 'admin' | 'agent';
}

function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const [step, setStep] = useState<'account' | 'agency'>('account');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Beta cerrada (auth 007): siempre visible y opcional — el backend decide
  const [inviteCode, setInviteCode] = useState('');
  // Consentimientos LFPDPPP para el registro vía Google (auth 007)
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [agencyName, setAgencyName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Invite flow state
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [inviteAgencyName, setInviteAgencyName] = useState('');
  const [inviteError, setInviteError] = useState('');

  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    invitesService.getInfo(inviteToken)
      .then(async (info) => {
        if (cancelled) return;
        setInvite(info);
        setEmail(info.email);
        try {
          const agency = await publicAgencyService.get(info.agency_id);
          if (!cancelled) setInviteAgencyName(agency.name);
        } catch { /* non-fatal: show banner without agency name */ }
      })
      .catch(() => {
        if (!cancelled) {
          setInviteError('La invitación es inválida o ha expirado. Puedes registrarte de forma normal.');
        }
      });
    return () => { cancelled = true; };
  }, [inviteToken]);

  // Cierre del flujo de invitación, compartido por el registro con contraseña y
  // con Google (gate R2: antes estaba duplicado y la rama Google sin test).
  // Devuelve true si ya navegó; false si hay que seguir al paso "agencia".
  const finishInviteFlow = async (token: string, info: InviteInfo): Promise<boolean> => {
    try {
      await invitesService.accept(token);
      navigate('/properties');
      return true;
    } catch (acceptErr: unknown) {
      // Desde QA 2026-10-23 el registro ya no reclama el invite por correo (lo
      // hace este accept); un 409 solo puede ser "ya perteneces a una agencia"
      // o "invitación ya reclamada" (p.ej. doble clic). Un 409 no significa
      // éxito por sí solo: se comprueba la agencia real.
      const acceptStatus = (acceptErr as { response?: { status?: number } })?.response?.status;
      if (acceptStatus !== 409) {
        setInviteError('No se pudo aceptar la invitación. Puedes crear tu agencia o pedir una nueva invitación.');
        return false;
      }
      let joinedAgencyId: string | null = null;
      try {
        joinedAgencyId = (await agencyService.getMe())?.agency?.id ?? null;
      } catch {
        joinedAgencyId = null;
      }
      if (joinedAgencyId === info.agency_id) {
        navigate('/properties');
        return true;
      }
      // Gate R2: no fingir éxito ni callar — el usuario ve en Agencia dónde quedó
      // y que la invitación del link no se aplicó.
      const invitedName = inviteAgencyName || 'la agencia del link';
      navigate('/admin/agency', {
        state: {
          notice: joinedAgencyId
            ? `La invitación a ${invitedName} no se aplicó: tu cuenta ya pertenece a otra agencia.`
            : `La invitación a ${invitedName} no se pudo confirmar. Revisa tu agencia o pide una nueva invitación.`,
        },
      });
      return true;
    }
  };

  const handleAccountSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Con link de invitación de agencia, su token sustituye al código beta
      // (QA 2026-10-23: el agente invitado quedaba bloqueado sin código)
      const response = await authService.register(
        email,
        password,
        inviteCode.trim(),
        invite && inviteToken ? inviteToken : undefined,
      );
      localStorage.setItem('access_token', response.access_token);
      if (invite && inviteToken) {
        // Invited users join the existing agency and skip the agency step
        if (await finishInviteFlow(inviteToken, invite)) return;
      }
      setStep('agency');
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        setError('Durante la beta el registro es solo por invitación. Ingresa tu código de invitación.');
      } else if (status === 409) {
        setError('Este email ya está registrado.');
      } else {
        setError('Error al registrar. Intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (idToken: string) => {
    setError('');
    // Sin pre-check local de consentimientos: una identidad de Google YA
    // registrada debe poder entrar desde aquí (el backend solo exige
    // consentimientos para cuentas nuevas y responde 422 en ese caso).
    setLoading(true);
    try {
      const response = await authService.googleLogin({
        id_token: idToken,
        invite_code: inviteCode.trim() || null,
        privacy_accepted: privacyAccepted,
        terms_accepted: termsAccepted,
        // Igual que el registro con contraseña: la invitación sustituye al código beta
        agency_invite_token: invite && inviteToken ? inviteToken : null,
      });
      localStorage.setItem('access_token', response.access_token);
      // Mismo flujo que el registro por password: quien llega desde un link
      // de invitación de agencia se une a esa agencia y salta el paso 2
      if (invite && inviteToken) {
        if (await finishInviteFlow(inviteToken, invite)) return;
      }
      setStep('agency');
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        setError('Durante la beta el registro es solo por invitación. Ingresa tu código de invitación.');
      } else if (status === 422) {
        setError('Para crear tu cuenta con Google, acepta el aviso de privacidad y los términos.');
      } else if (status === 503) {
        setError('El registro con Google no está disponible por ahora. Usa email y contraseña.');
      } else {
        setError('No se pudo completar el registro con Google. Intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAgencySubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await agencyService.create(agencyName);
      navigate('/properties');
    } catch {
      setError('Error al crear la agencia. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex">
      {/* Left panel */}
      <div
        className="hidden lg:flex lg:w-[58%] flex-col justify-between p-14 relative overflow-hidden"
        style={{ background: 'radial-gradient(ellipse at 20% 80%, #263329 0%, #0E1510 100%)' }}
      >
        <div
          className="absolute inset-0 opacity-[0.055]"
          style={{
            backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />
        <div className="absolute top-0 left-0 w-[3px] h-full bg-primary/50" />

        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-primary" />
          </div>
          <span className="text-white font-semibold text-lg tracking-tight">Takarah</span>
        </div>

        <div className="relative z-10 space-y-8">
          <div className="space-y-4">
            <h2 className="text-white text-[2.4rem] font-semibold leading-[1.15] tracking-tight">
              Empieza a vender<br />
              <span className="text-stone-500">de forma más</span><br />
              inteligente.
            </h2>
            <p className="text-stone-500 text-base max-w-sm leading-relaxed">
              Crea tu cuenta, configura tu agencia y empieza a compartir propiedades en minutos.
            </p>
          </div>

          <div className="space-y-4">
            {[
              { icon: Zap, text: 'Setup en menos de 2 minutos' },
              { icon: Users, text: 'Invita a tu equipo de agentes' },
              { icon: Shield, text: 'Links seguros y revocables' },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  <Icon className="w-3.5 h-3.5 text-stone-400" />
                </div>
                <span className="text-stone-400 text-sm">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <p className="text-stone-700 text-xs">
            Al registrarte aceptas nuestros Términos de Servicio y Política de Privacidad.
          </p>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 lg:px-16 py-12 bg-background">
        <div className="flex items-center gap-2 mb-12 lg:hidden">
          <Building2 className="w-5 h-5 text-primary" />
          <span className="font-semibold tracking-tight">Takarah</span>
        </div>

        <div className="w-full max-w-sm">
          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-8">
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                <span className="text-[10px] font-semibold text-white">1</span>
              </div>
              <span className={`text-xs font-medium ${step === 'account' ? 'text-foreground' : 'text-muted-foreground'}`}>
                Cuenta
              </span>
            </div>
            <div className="h-px w-8 bg-border" />
            <div className="flex items-center gap-1.5">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center border transition-colors ${step === 'agency' ? 'bg-primary border-primary' : 'bg-background border-border'}`}>
                <span className={`text-[10px] font-semibold ${step === 'agency' ? 'text-white' : 'text-muted-foreground'}`}>2</span>
              </div>
              <span className={`text-xs font-medium ${step === 'agency' ? 'text-foreground' : 'text-muted-foreground'}`}>
                Agencia
              </span>
            </div>
          </div>

          {step === 'account' ? (
            <>
              <h1 className="text-2xl font-semibold tracking-tight mb-1">Crea tu cuenta</h1>
              <p className="text-sm text-muted-foreground mb-8">Paso 1 de 2 — Datos de acceso</p>

              {invite && (
                <div className="mb-6 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-foreground">
                  Te invitaron a unirte a <span className="font-semibold">{inviteAgencyName || "una agencia"}</span>
                </div>
              )}
              {inviteError && (
                <div className="mb-6 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                  {inviteError}
                </div>
              )}

              <form onSubmit={handleAccountSubmit} className="space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    required
                    disabled={!!invite}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Contraseña</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 8 caracteres"
                    required
                    minLength={8}
                  />
                </div>
                {/* Quien llega por invitación de agencia no necesita el código beta */}
                {!invite && (
                  <div className="space-y-1.5">
                    <Label htmlFor="invite-code">Código de invitación (beta cerrada)</Label>
                    <Input
                      id="invite-code"
                      type="text"
                      value={inviteCode}
                      onChange={(e) => setInviteCode(e.target.value)}
                      placeholder="El código que te compartió el equipo de Takarah"
                    />
                  </div>
                )}
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button type="submit" className="w-full transition-all active:scale-[0.98]" disabled={loading}>
                  {loading ? 'Creando cuenta...' : 'Continuar'}
                </Button>
                <GoogleSignInButton onCredential={handleGoogleCredential} text="signup_with" divider="o" />
                {/* Consentimientos solo del flujo Google: fail-soft simétrico
                    al botón — sin GOOGLE_CLIENT_ID no se muestran */}
                {GOOGLE_CLIENT_ID && (
                  <div className="space-y-2 text-left">
                    <label className="flex items-start gap-2 text-xs text-muted-foreground">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={privacyAccepted}
                        onChange={(e) => setPrivacyAccepted(e.target.checked)}
                      />
                      <span>Acepto el aviso de privacidad (requerido para registrarte con Google).</span>
                    </label>
                    <label className="flex items-start gap-2 text-xs text-muted-foreground">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                      />
                      <span>Acepto los términos y condiciones (requerido para registrarte con Google).</span>
                    </label>
                  </div>
                )}
                <p className="text-center text-sm text-muted-foreground">
                  ¿Ya tienes cuenta?{' '}
                  <Link to="/login" className="text-foreground font-medium hover:text-primary transition-colors">
                    Inicia sesión
                  </Link>
                </p>
              </form>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-semibold tracking-tight mb-1">Crea tu agencia</h1>
              <p className="text-sm text-muted-foreground mb-8">Paso 2 de 2 — Configura tu inmobiliaria</p>
              <form onSubmit={handleAgencySubmit} className="space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="agency-name">Nombre de la agencia</Label>
                  <Input
                    id="agency-name"
                    type="text"
                    value={agencyName}
                    onChange={(e) => setAgencyName(e.target.value)}
                    placeholder="Inmobiliaria Ejemplo S.A."
                    required
                    minLength={2}
                  />
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button type="submit" className="w-full transition-all active:scale-[0.98]" disabled={loading}>
                  {loading ? 'Creando agencia...' : 'Crear agencia'}
                </Button>
                <button
                  type="button"
                  onClick={() => navigate('/properties')}
                  disabled={loading}
                  className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
                >
                  Omitir por ahora
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
